import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import type { SpellcheckIssue } from '@/api/spellcheck';
import { getProseMirrorIssueRanges, SPELLCHECK_TYPO_CLASS } from '@/lib/spellcheck';

type SpellcheckPluginMeta = {
    issues: SpellcheckIssue[];
};

export const spellcheckPluginKey = new PluginKey<DecorationSet>('laudopsiSpellcheck');

const buildDecorations = (
    doc: Parameters<typeof getProseMirrorIssueRanges>[0],
    issues: SpellcheckIssue[],
) => {
    const decorations = getProseMirrorIssueRanges(doc, issues).map(({ from, to, issueIndex }) =>
        Decoration.inline(from, to, {
            class: SPELLCHECK_TYPO_CLASS,
            'data-spellcheck-issue-index': String(issueIndex),
        }),
    );

    return DecorationSet.create(doc, decorations);
};

export const createSpellcheckPlugin = () =>
    new Plugin<DecorationSet>({
        key: spellcheckPluginKey,
        state: {
            init: () => DecorationSet.empty,
            apply(transaction, previousDecorations, _oldState, newState) {
                const meta = transaction.getMeta(spellcheckPluginKey) as
                    | SpellcheckPluginMeta
                    | undefined;

                if (meta) {
                    return buildDecorations(newState.doc, meta.issues);
                }

                if (transaction.docChanged) {
                    return previousDecorations.map(transaction.mapping, transaction.doc);
                }

                return previousDecorations;
            },
        },
        props: {
            decorations(state) {
                return spellcheckPluginKey.getState(state) ?? DecorationSet.empty;
            },
        },
    });
